import { useMemo } from 'react';
import NumberFormat from 'react-number-format';
import { Link } from 'react-router-dom';

import Moment from 'moment-timezone';
import queryString from 'query-string';

import { useGlobalAuthStore } from '@Stores/global/auth';
import LossesStore from 'modules/report/stores/losses'
import surveysStore from 'modules/report/stores/surveys';
import appStore from 'stores/app'
import { formatDate, formatDateTime } from 'utils'
import history from 'utils/history';

const keys = [
  'sweepId',
  'contestId',
  'first_name',
  'selection_date',
  'winner_status',
  'fulfillment',
];

function getUrlLink(row, isSuperAdmin) {
  if (row.key === 'post' || row.key === 'post_target') {
    const { post: { objectsMapping } } = LossesStore
    if (objectsMapping[row.value]) return `/cms/${objectsMapping[row.value].content_type}/${row.value}`;
    if (objectsMapping[row.value]) return `/cms/${objectsMapping[row.value].content_type}/${row.value}`;
    if (objectsMapping[row.value]) return `/cms/${objectsMapping[row.value].content_type}/${row.value}`;

    return '/cms'
  }

  if (row.key === 'site' || row.key === 'organization') {
    if (!isSuperAdmin) return `/reports`
    return `/admin/${row.key}s/${row.value}`
  }

  if (row.key === 'path' || row.key === 'pathUserProfile' || row.key === 'page') {
    return `/path/${row.key === 'pathUserProfile' ? 'dashboard/user_profile' : `${row.key}s`}/${row.value}`;
  }

  if (row.key === 'customField') {
    const { customField: { objectsMapping } } = surveysStore;
    if (objectsMapping[row.value]) return `/path/fields/${objectsMapping[row.value].id}`;

    return '/path/fields';
  }

  if (row.key === 'rev__integrationId' || row.key === 'integration') return `/settings/integrations/${row.value}`;

  if (row.key === 'form') return `/offer/forms/${row.value}/fields`;

  return `/offer/${`${row.key}s`}/${row.value}`;
}

function weekOfMonthFormatter(dateValue) {
  const date = Moment(dateValue).tz(appStore.timezone);
  const endOfWeek = date.clone().endOf('week');
  const sameMonth = date.month() === endOfWeek.month();

  return `${`${date.startOf('week').format('MMM Do')} to ${endOfWeek.format(sameMonth ? 'Do' : 'MMM Do')}`}`;
}

function LinkName({ row }) {
  const { auth: { isSuperAdmin } } = useGlobalAuthStore()
  const link = useMemo(() => getUrlLink(row, isSuperAdmin), [row, isSuperAdmin])

  return <Link to={link} className="text-secondary">
    {row.value === '' ? '--' : row.value}{row.name ? ` - ${row.name}` : ''}
  </Link>
}

const formatters = {
  Loadable: ({ isLoading, children }) => <span>{children} {isLoading && <small><i>Loading</i></small>}</span>,

  money(decimalScale = 2) {
    return cell => {
      if (cell === null || cell === undefined) {
        return '--'
      }

      return <NumberFormat
        value={Number(cell).toFixed(decimalScale)}
        displayType="text"
        thousandSeparator=","
        decimalSeparator="."
        prefix={'$'}
        decimalScale={decimalScale}
        fixedDecimalScale={true}
      />
    }
  },

  number(cell) {
    if (cell === null || cell === undefined) {
      return '--'
    }

    return <NumberFormat
      value={cell.toFixed(2)}
      displayType="text"
      thousandSeparator=","
      decimalSeparator="."
      prefix={''}
      decimalScale={0}
      fixedDecimalScale={true}
    />
  },

  percent(cell, total = 1) {
    if (cell === null || cell === undefined) {
      return '--'
    }

    return <NumberFormat
      value={(cell / total) * 100}
      displayType="text"
      decimalSeparator="."
      suffix={'%'}
      decimalScale={2}
      fixedDecimalScale={true}
    />
  },

  parentPercent(formatter, dataField = 'total') {
    return (cell, row) => {
      const parentPercent = cell != null && row.parent && row.parent[dataField] && <small>
        &nbsp; - &nbsp;<i>{formatters.percent(row[dataField] / row.parent[dataField])}</i>
      </small>
      return <>
        {formatter(cell)}{parentPercent || null}
      </>
    }
  },

  name(_, row) {
    if (row.formattedName) {
      return row.formattedName
    }

    if (row.key === 'day' || row.key === 'day_session') {
      return row.formattedName = formatDate(parseInt(row.value))
    }

    if (row.key === 'hour') {
      return row.formattedName = formatDateTime(parseInt(row.value))
    }

    if (row.key === 'month' || row.key === 'month_session') {
      return row.formattedName = formatDate(parseInt(row.value), 'MM/YYYY')
    }
    if (row.key === 'week' || row.key === 'week_session') {
      return row.formattedName = weekOfMonthFormatter(parseInt(row.value))
    }
    if (row.value != 0 && keys.includes(row.key)) {
      return <LinkName row={row} />
    }
    return `${row.value === '' ? '--' : row.value}${row.name ? ` - ${row.name}` : ''}`
  },

  groupsText(group) {
    return group.replace(/__/g, '. ').replace(/_/g, '-').replace(/([a-z])([A-Z])/g, ($, $1, $2) => `${$1}-${$2}`)
  },

  fallback(cell) {
    return cell >= 0 ? cell : '--'
  },

  colorize: (cell, value) => <span className={value < 0 ? 'text-danger' : 'text-success'}>{cell}</span>,

  drilldown(onClick, dataField) {
    return (cell, row) => {
      if (cell === null || cell === undefined) {
        return '--'
      }

      return <>
        <span className="btn-link text-secondary cursor-pointer">
          <NumberFormat
            onClick={event => onClick(event, dataField, row)}
            value={cell.toFixed(2)}
            displayType="text"
            thousandSeparator=","
            decimalSeparator="."
            prefix={''}
            decimalScale={0}
            fixedDecimalScale={true}
          />
        </span>
        {cell !== null && row.parent && row.parent[dataField] ? <small> &nbsp; - &nbsp;<i>{formatters.percent(row[dataField] / row.parent[dataField])}</i> </small> : null}
      </>
    }
  },

  openNewTab(pathname, params) {
    const handleClick = () => {
      try {
        const query = btoa(JSON.stringify(params))
        const search = queryString.stringify({ configure: query })
        window.open(history.createHref({ pathname, search }))
      } catch (error) {
        console.error(error)
      }
    }

    return <a href='#'
      className="text-secondary"
      onClick={() => handleClick()}
    >
      {params.value}
    </a>
  },

  drilldownDB(onClick, { params, label, pathname }) {
    return <span className="btn-link cursor-pointer" onClick={event => onClick(event, params, pathname)}>
      {label}
    </span>
  },

  newTabDrilldownDB(event, params, pathname) {
    try {
      event.stopPropagation()

      const state = JSON.stringify(params)
      const configure = btoa(state)
      const search = queryString.stringify({ configure })

      const href = history.createHref({
        pathname: `${history.location.pathname}${pathname}`,
        search,
      })
      window.open(href)
    } catch (error) {
      console.log(error)
    }
  },
}

export {
  formatters,
  weekOfMonthFormatter,
}
